import { useEffect, useState } from "react";
import { Plus, Trash2, TrendingUp, TrendingDown, FileClock } from "lucide-react";
import { api } from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";

export default function Journal() {
    const [trades, setTrades] = useState([]);
    const [instruments, setInstruments] = useState([]);
    const [stats, setStats] = useState({ live: {}, backtest: {} });
    const [filter, setFilter] = useState("live"); // live | backtest
    const [open, setOpen] = useState(false);

    const load = async () => {
        const [t, i, s] = await Promise.all([api.get("/trades"), api.get("/instruments"), api.get("/stats/trades")]);
        setTrades(t.data.sort((a, b) => (b.date || "").localeCompare(a.date || "")));
        setInstruments(i.data);
        setStats(s.data);
    };
    useEffect(() => { load(); }, []);

    const del = async (id) => {
        await api.delete(`/trades/${id}`);
        load();
    };

    const list = trades.filter((t) => (filter === "live" ? !t.is_backtest : t.is_backtest));
    const current = filter === "live" ? stats.live : stats.backtest;

    return (
        <div className="space-y-8" data-testid="journal-page">
            <PageHeader eyebrow="Learn from every trade" title="Journal & Backtesting" subtitle="Live trades build your record. Backtests build your conviction. Keep them separate." />

            <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label="Trades" value={current.count || 0} />
                <StatCard label="Win rate" value={`${Math.round(current.win_rate || 0)}%`} />
                <StatCard label="Total R" value={current.total_r || 0} accent />
                <StatCard label="Avg R" value={current.avg_r || 0} />
                <StatCard label="Wins / Losses" value={`${current.wins || 0} / ${current.losses || 0}`} />
            </section>

            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex gap-2" data-testid="journal-tabs">
                    {[
                        { id: "live", label: "Live trades" },
                        { id: "backtest", label: "Backtests" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setFilter(t.id)}
                            data-testid={`tab-${t.id}`}
                            className={`px-4 py-2 text-sm rounded-md transition-colors ${
                                filter === t.id ? "bg-[#D4AF37] text-[#0E0E10]" : "text-zinc-400 border border-white/10 hover:border-[#D4AF37]/40"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <button onClick={() => setOpen(!open)} className="bg-[#D4AF37] text-[#0E0E10] px-3 py-2 rounded-md hover:bg-[#B3922E] text-sm inline-flex items-center gap-2" data-testid="trade-new-btn">
                    <Plus className="w-4 h-4" /> Log {filter === "live" ? "a trade" : "a backtest"}
                </button>
            </div>

            {open && <TradeForm instruments={instruments} isBacktest={filter === "backtest"} onSaved={() => { load(); setOpen(false); }} />}

            <section className="space-y-3" data-testid="trade-list">
                {list.length === 0 && <div className="text-sm text-zinc-500">No {filter === "live" ? "trades" : "backtests"} yet.</div>}
                {list.map((t) => (
                    <TradeCard key={t.id} trade={t} onDelete={() => del(t.id)} />
                ))}
            </section>
        </div>
    );
}

function StatCard({ label, value, accent }) {
    return (
        <div className="twp-card p-4">
            <div className="caption">{label}</div>
            <div className={`font-serif text-2xl mt-1 ${accent ? "gold" : ""}`}>{value}</div>
        </div>
    );
}

function TradeForm({ instruments, isBacktest, onSaved }) {
    const [form, setForm] = useState({
        instrument: "", date: new Date().toISOString().slice(0, 10), setup: "", strategy: "", timeframe: "",
        entry_notes: "", exit_notes: "", stop: "", target: "", r_multiple: "", rule_adherence: 8, lessons: "", screenshot_path: "",
    });
    const [uploading, setUploading] = useState(false);

    const upload = async (file) => {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm({ ...form, screenshot_path: data.id });
        } finally { setUploading(false); }
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.instrument || !form.date) return;
        await api.post("/trades", {
            ...form,
            r_multiple: form.r_multiple === "" ? null : parseFloat(form.r_multiple),
            rule_adherence: parseInt(form.rule_adherence) || null,
            is_backtest: isBacktest,
        });
        onSaved();
    };

    return (
        <form onSubmit={save} className="twp-card p-6 grid md:grid-cols-3 gap-3" data-testid="trade-form">
            <select value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} required className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="trade-instrument">
                <option value="">Instrument…</option>
                {instruments.map((i) => <option key={i.id} value={i.symbol}>{i.symbol}</option>)}
            </select>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="trade-date" />
            <input placeholder="Timeframe (e.g. 15m)" value={form.timeframe} onChange={(e) => setForm({ ...form, timeframe: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
            <input placeholder="Setup (e.g. Opening range breakout)" value={form.setup} onChange={(e) => setForm({ ...form, setup: e.target.value })} className="md:col-span-2 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="trade-setup" />
            <input placeholder="Strategy name" value={form.strategy} onChange={(e) => setForm({ ...form, strategy: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
            <input placeholder="Stop" value={form.stop} onChange={(e) => setForm({ ...form, stop: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
            <input placeholder="Target" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
            <input placeholder="R-multiple (e.g. 1.5 or -1)" type="number" step="0.01" value={form.r_multiple} onChange={(e) => setForm({ ...form, r_multiple: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="trade-r" />
            <textarea placeholder="Entry notes" value={form.entry_notes} onChange={(e) => setForm({ ...form, entry_notes: e.target.value })} rows={2} className="md:col-span-3 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
            <textarea placeholder="Exit notes" value={form.exit_notes} onChange={(e) => setForm({ ...form, exit_notes: e.target.value })} rows={2} className="md:col-span-3 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
            <textarea placeholder="Lessons learned" value={form.lessons} onChange={(e) => setForm({ ...form, lessons: e.target.value })} rows={2} className="md:col-span-3 bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="trade-lessons" />
            <div className="flex items-center gap-3">
                <label className="caption">Rule adherence</label>
                <input type="range" min={1} max={10} value={form.rule_adherence} onChange={(e) => setForm({ ...form, rule_adherence: e.target.value })} className="flex-1" />
                <span className="font-mono gold w-6 text-right">{form.rule_adherence}</span>
            </div>
            <div className="flex items-center gap-3">
                <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} className="text-sm text-zinc-400" />
                {uploading && <span className="text-xs text-zinc-500">Uploading…</span>}
                {form.screenshot_path && <span className="text-xs gold">Attached ✓</span>}
            </div>
            <button type="submit" className="bg-[#D4AF37] text-[#0E0E10] px-4 py-2 rounded-md hover:bg-[#B3922E] text-sm" data-testid="trade-save">Save</button>
        </form>
    );
}

function TradeCard({ trade, onDelete }) {
    const r = trade.r_multiple;
    const positive = r != null && r > 0;
    const negative = r != null && r < 0;
    return (
        <div className="twp-card p-5" data-testid={`trade-${trade.id}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono gold text-sm">{trade.instrument}</span>
                    <span className="text-xs text-zinc-500">{trade.date}</span>
                    {trade.setup && <span className="text-xs px-2 py-0.5 bg-white/5 rounded">{trade.setup}</span>}
                    {trade.timeframe && <span className="text-xs text-zinc-500">· {trade.timeframe}</span>}
                    {trade.is_backtest && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37] uppercase tracking-wider border border-[#D4AF37]/30">Backtest</span>}
                </div>
                <div className="flex items-center gap-3">
                    {r != null && (
                        <div className={`flex items-center gap-1 font-mono text-sm ${positive ? "text-[#5B8A62]" : negative ? "text-[#B95050]" : "text-zinc-400"}`}>
                            {positive ? <TrendingUp className="w-4 h-4" strokeWidth={1.5} /> : <TrendingDown className="w-4 h-4" strokeWidth={1.5} />}
                            {r > 0 ? `+${r}R` : `${r}R`}
                        </div>
                    )}
                    <button onClick={onDelete} className="text-zinc-500 hover:text-[#B95050]"><Trash2 className="w-4 h-4" /></button>
                </div>
            </div>
            {(trade.entry_notes || trade.exit_notes) && (
                <div className="mt-3 grid md:grid-cols-2 gap-3 text-sm">
                    {trade.entry_notes && <div><span className="caption">Entry</span><div className="text-zinc-300 mt-0.5">{trade.entry_notes}</div></div>}
                    {trade.exit_notes && <div><span className="caption">Exit</span><div className="text-zinc-300 mt-0.5">{trade.exit_notes}</div></div>}
                </div>
            )}
            {trade.lessons && <div className="mt-3 text-sm"><span className="caption">Lessons</span><div className="text-zinc-300 mt-0.5">{trade.lessons}</div></div>}
            {trade.rule_adherence != null && (
                <div className="mt-3 flex items-center gap-2 caption">
                    <FileClock className="w-3.5 h-3.5" /> Rule adherence
                    <span className="font-mono text-[#D4AF37]">{trade.rule_adherence}/10</span>
                </div>
            )}
        </div>
    );
}
